import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { createRfq, assignRfqPartners } from '../../api/rfqs';
import { fetchBrandList } from '../../api/brands';
import { fetchPartners } from '../../api/partners';

const initialProductionState = {
  targetUnits: '',
  minimumUnits: '',
  maximumUnits: '',
  sizeCurve: '',
  materials: '',
  trims: '',
  processes: '',
  sustainability: '',
  compliance: '',
};

const initialShippingState = {
  incoterms: '',
  destination: '',
  packaging: '',
  specialInstructions: '',
};

const initialFulfillmentState = {
  strategy: '',
  notes: '',
};

const initialQualityState = {
  standards: '',
  notes: '',
};

const CreateRFQPage = () => {
  const navigate = useNavigate();

  const [status, setStatus] = useState('draft');
  const [title, setTitle] = useState('');
  const [code, setCode] = useState('');
  const [brandId, setBrandId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [production, setProduction] = useState(initialProductionState);
  const [shipping, setShipping] = useState(initialShippingState);
  const [fulfillment, setFulfillment] = useState(initialFulfillmentState);
  const [quality, setQuality] = useState(initialQualityState);
  const [responseDue, setResponseDue] = useState('');
  const [decisionDate, setDecisionDate] = useState('');
  const [dropDate, setDropDate] = useState('');
  const [techpackYaml, setTechpackYaml] = useState('');
  const [techpackSummary, setTechpackSummary] = useState('');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);

  const [brands, setBrands] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [error, setError] = useState('');

  const addAttachment = () => {
    setAttachments((prev) => [
      ...prev,
      { name: '', url: '', type: '' },
    ]);
  };

  const updateAttachment = (index, key, value) => {
    setAttachments((prev) =>
      prev.map((item, idx) =>
        idx === index ? { ...item, [key]: value } : item
      )
    );
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== index));
  };

  const loadOptions = async () => {
    try {
      setLoadingOptions(true);
      const [brandsResponse, partnersResponse] = await Promise.all([
        fetchBrandList(),
        fetchPartners(),
      ]);
      setBrands(brandsResponse?.brands || brandsResponse || []);
      setPartners(partnersResponse || []);
    } catch (err) {
      console.error('Failed to load brands/partners', err);
      toast.error('Failed to load brand or partner options.');
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    loadOptions();
  }, []);

  // helper safe parse
  const parseSummary = (value) => {
    if (!value) return undefined;
    try {
      return JSON.parse(value);
    } catch (err) {
      return value;
    }
  };

  const handleSubmitWithPayload = async (event) => {
    event.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    if (!responseDue) {
      setError('Response due date is required.');
      return;
    }

    const brand = brands.find((b) => (b._id || b.id) === brandId);

    const payload = {
      title: title.trim(),
      code: code.trim() || undefined,
      status,
      brand: brand
        ? { id: brand._id || brand.id, name: brand.name }
        : brandId
          ? { id: brandId }
          : undefined,
      campaign: campaignName
        ? { id: campaignId || undefined, name: campaignName }
        : undefined,
      requirements: {
        production: {
          targetUnits: production.targetUnits ? Number(production.targetUnits) : undefined,
          minimumUnits: production.minimumUnits ? Number(production.minimumUnits) : undefined,
          maximumUnits: production.maximumUnits ? Number(production.maximumUnits) : undefined,
          sizeCurve: production.sizeCurve
            ? production.sizeCurve.split(',').map((item) => item.trim())
            : [],
          materials: production.materials
            ? production.materials.split(',').map((item) => item.trim())
            : [],
          trims: production.trims
            ? production.trims.split(',').map((item) => item.trim())
            : [],
          processes: production.processes
            ? production.processes.split(',').map((item) => item.trim())
            : [],
          sustainability: production.sustainability
            ? production.sustainability.split(',').map((item) => item.trim())
            : [],
          compliance: production.compliance
            ? production.compliance.split(',').map((item) => item.trim())
            : [],
        },
        shipping: shipping,
        fulfillment: fulfillment,
        quality: quality,
      },
      techpack: {
        yaml: techpackYaml || undefined,
        summary: parseSummary(techpackSummary),
        attachments: attachments.filter((item) => item.url),
      },
      deadlines: {
        responseDue: responseDue ? new Date(responseDue) : undefined,
        decisionDate: decisionDate ? new Date(decisionDate) : undefined,
        dropDate: dropDate ? new Date(dropDate) : undefined,
      },
      notes: notes || undefined,
    };

    try {
      setLoading(true);
      const rfq = await createRfq(payload);

      if (selectedPartners.length > 0) {
        await assignRfqPartners(rfq._id || rfq.id, selectedPartners);
      }

      toast.success('RFQ created successfully');
      navigate(`/rfqs/${rfq._id || rfq.id}`);
    } catch (err) {
      console.error('Failed to create RFQ', err);
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to create RFQ. Please try again.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const availablePartners = useMemo(
    () =>
      partners.map((partner) => ({
        id: partner._id || partner.id,
        name: partner.legalName || partner.company?.legalName || partner.name,
        tier: partner.tier,
      })),
    [partners]
  );

  return (
    <Box component="form" onSubmit={handleSubmitWithPayload} sx={{ display: 'grid', gap: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Create RFQ
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Define sourcing requirements, invite partners, and prepare to evaluate bids.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/rfqs')}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Create RFQ
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error">{error}</Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Overview
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="RFQ Code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              fullWidth
              placeholder="Optional"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              label="Status"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              fullWidth
            >
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="issued">Issued</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              select
              label="Brand"
              value={brandId}
              onChange={(event) => setBrandId(event.target.value)}
              fullWidth
              disabled={loadingOptions}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {brands.map((brand) => (
                <MenuItem key={brand._id || brand.id} value={brand._id || brand.id}>
                  {brand.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Campaign Name"
              value={campaignName}
              onChange={(event) => setCampaignName(event.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Campaign ID"
              value={campaignId}
              onChange={(event) => setCampaignId(event.target.value)}
              fullWidth
              placeholder="Optional"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Production Requirements
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Target units"
              value={production.targetUnits}
              onChange={(event) =>
                setProduction((prev) => ({ ...prev, targetUnits: event.target.value }))
              }
              fullWidth
              InputProps={{ inputMode: 'numeric' }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Minimum units"
              value={production.minimumUnits}
              onChange={(event) =>
                setProduction((prev) => ({ ...prev, minimumUnits: event.target.value }))
              }
              fullWidth
              InputProps={{ inputMode: 'numeric' }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Maximum units"
              value={production.maximumUnits}
              onChange={(event) =>
                setProduction((prev) => ({ ...prev, maximumUnits: event.target.value }))
              }
              fullWidth
              InputProps={{ inputMode: 'numeric' }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Size curve"
              value={production.sizeCurve}
              helperText="Comma-separated (e.g. XS:20,S:40,M:60)"
              onChange={(event) =>
                setProduction((prev) => ({ ...prev, sizeCurve: event.target.value }))
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Materials"
              value={production.materials}
              helperText="Comma-separated list"
              onChange={(event) =>
                setProduction((prev) => ({ ...prev, materials: event.target.value }))
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Trims"
              value={production.trims}
              helperText="Comma-separated list"
              onChange={(event) =>
                setProduction((prev) => ({ ...prev, trims: event.target.value }))
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Processes"
              value={production.processes}
              helperText="Comma-separated list"
              onChange={(event) =>
                setProduction((prev) => ({ ...prev, processes: event.target.value }))
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Sustainability requirements"
              value={production.sustainability}
              helperText="Comma-separated list"
              onChange={(event) =>
                setProduction((prev) => ({ ...prev, sustainability: event.target.value }))
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Compliance requirements"
              value={production.compliance}
              helperText="Comma-separated list"
              onChange={(event) =>
                setProduction((prev) => ({ ...prev, compliance: event.target.value }))
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Shipping & Fulfillment
            </Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Incoterms / shipping terms"
                value={shipping.incoterms}
                onChange={(event) =>
                  setShipping((prev) => ({ ...prev, incoterms: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Destination"
                value={shipping.destination}
                onChange={(event) =>
                  setShipping((prev) => ({ ...prev, destination: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Packaging"
                value={shipping.packaging}
                onChange={(event) =>
                  setShipping((prev) => ({ ...prev, packaging: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Special instructions"
                value={shipping.specialInstructions}
                onChange={(event) =>
                  setShipping((prev) => ({ ...prev, specialInstructions: event.target.value }))
                }
                fullWidth
                multiline
                minRows={2}
              />
              <Divider />
              <TextField
                label="Fulfillment strategy"
                value={fulfillment.strategy}
                onChange={(event) =>
                  setFulfillment((prev) => ({ ...prev, strategy: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Fulfillment notes"
                value={fulfillment.notes}
                onChange={(event) =>
                  setFulfillment((prev) => ({ ...prev, notes: event.target.value }))
                }
                fullWidth
                multiline
                minRows={2}
              />
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Quality & Compliance
            </Typography>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Quality standards"
                value={quality.standards}
                onChange={(event) =>
                  setQuality((prev) => ({ ...prev, standards: event.target.value }))
                }
                fullWidth
              />
              <TextField
                label="Notes"
                value={quality.notes}
                onChange={(event) =>
                  setQuality((prev) => ({ ...prev, notes: event.target.value }))
                }
                fullWidth
                multiline
                minRows={3}
              />
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Tech Pack
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Tech pack YAML"
              value={techpackYaml}
              onChange={(event) => setTechpackYaml(event.target.value)}
              fullWidth
              multiline
              minRows={8}
              placeholder="Paste YAML generated by the design pipeline"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Tech pack summary (optional JSON)"
              value={techpackSummary}
              onChange={(event) => setTechpackSummary(event.target.value)}
              fullWidth
              multiline
              minRows={8}
              placeholder='{ "silhouette": "OVERSIZED_TEE", ... }'
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Attachments
        </Typography>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {attachments.map((attachment, index) => (
            <Paper
              key={index}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2 }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Name"
                    value={attachment.name}
                    onChange={(event) => updateAttachment(index, 'name', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="URL"
                    value={attachment.url}
                    onChange={(event) => updateAttachment(index, 'url', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Type"
                    value={attachment.type}
                    onChange={(event) => updateAttachment(index, 'type', event.target.value)}
                    fullWidth
                    placeholder="image/png"
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <IconButton color="error" onClick={() => removeAttachment(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Paper>
          ))}
          <Button startIcon={<AddIcon />} onClick={addAttachment} variant="outlined">
            Add attachment
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Timelines & Notes
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Response due"
              type="date"
              value={responseDue}
              onChange={(event) => setResponseDue(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              required
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Decision date"
              type="date"
              value={decisionDate}
              onChange={(event) => setDecisionDate(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Drop date"
              type="date"
              value={dropDate}
              onChange={(event) => setDropDate(event.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Internal notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Invite Partners
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Select the partners you want to notify. Invitations can be managed later from the RFQ detail view.
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 2, flexWrap: 'wrap' }}
        >
          {availablePartners.map((partner) => {
            const isSelected = selectedPartners.includes(partner.id);
            return (
              <Chip
                key={partner.id}
                label={`${partner.name || 'Partner'}${partner.tier ? ` • ${partner.tier}` : ''}`}
                color={isSelected ? 'primary' : 'default'}
                onClick={() =>
                  setSelectedPartners((prev) =>
                    prev.includes(partner.id)
                      ? prev.filter((id) => id !== partner.id)
                      : [...prev, partner.id]
                  )
                }
                sx={{ mr: 1, mb: 1 }}
                variant={isSelected ? 'filled' : 'outlined'}
              />
            );
          })}
          {availablePartners.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No partners available. Add partners in the Partner Network section first.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default CreateRFQPage;
